/**
 * csp-style-shim.js
 * CSP 阶段二整改: 内容内联样式 CSSOM 化 —— 使 CSP 可完全去掉 style 系指令的 'unsafe-inline'。
 *
 * 原理: CSP 只拦"解析时刻"的内联样式(HTML 字符串里的 style 属性与 <style> 块), 对 CSSOM 写入
 * (el.style.cssText / 可构造样式表 adoptedStyleSheets)不限制。故分两层:
 *   ① 注入前(字符串层) deinlineStyles: style 属性 → data-csp-style, <style> 块 → 惰性 <template>;
 *   ② 注入后(DOM 层) applyDeinlinedStyles: CSSOM 回填。cssText 写回会反射为 style 属性串,
 *      最终 DOM 与改造前一致 —— html2canvas/jspdf 报告导出与 outerHTML 序列化(在线编辑器保存链路,
 *      经 restoreCanonicalHtml 还原)均不受影响。
 * 本地 dev(无 CSP 头)环境下两层皆为无副作用直通。
 */

var DATA_STYLE = 'data-csp-style'
var DATA_BLOCK = 'data-csp-style-block'

/**
 * 字符串预处理: 避开 CSP 解析拦截。先处理 <style> 块再处理 style 属性(防止 CSS 文本里的
 * 类属性字样被误改)。幂等: 产物中不再存在原形态, 重复执行无变化。
 */
export function deinlineStyles(html) {
  if (!html || html.indexOf('style') === -1) return html
  var out = html
  // <style ...>css</style> → <template data-csp-style-block ...>css</template>
  // template 内容惰性解析不构成样式表, 不触发 CSP; 原属性(type/media)原样保留供回填读取
  out = out.replace(/<style\b([^>]*)>([\s\S]*?)<\/style>/gi,
    '<template ' + DATA_BLOCK + '$1>$2<\/template>')
  // 标签开区内的 style="..." → data-csp-style="..."(值内无裸双引号, 原样直通)
  out = out.replace(/(<[a-zA-Z][^<>]*?)\sstyle\s*=\s*"([^"]*)"/gi, '$1 ' + DATA_STYLE + '="$2"')
  // 单引号形态: 值内可能含双引号, 转义为 &quot; 后并入双引号属性
  out = out.replace(/(<[a-zA-Z][^<>]*?)\sstyle\s*=\s*'([^']*)'/gi, function (m, tag, val) {
    return tag + ' ' + DATA_STYLE + '="' + val.replace(/"/g, '&quot;') + '"'
  })
  return out
}

/** 读取 template 承载的 CSS 文本: template 解析内容在 .content 片段中, textContent 恒为空 */
function templateCssText(t) {
  return (t.content ? t.content.textContent : '') || t.textContent || ''
}

/**
 * DOM 层回填: 可在任何 document 上下文(父页面/iframe 内)执行, 可重复执行。
 * 回填 style 属性后移除标记属性 → outerHTML 序列化天然还原为规范 HTML;
 * <style> 块经可构造样式表落地, template 节点保留在 DOM 中携带 CSS 文本供序列化还原。
 */
export function applyDeinlinedStyles(root) {
  if (!root || !root.querySelectorAll) return
  var marked = root.querySelectorAll('[' + DATA_STYLE + ']')
  for (var i = 0; i < marked.length; i++) {
    var el = marked[i]
    var v = el.getAttribute(DATA_STYLE)
    if (v) el.style.cssText = v
    el.removeAttribute(DATA_STYLE)
  }
  var blocks = root.querySelectorAll('template[' + DATA_BLOCK + ']')
  for (var j = 0; j < blocks.length; j++) {
    adoptStyleText(blocks[j].ownerDocument, templateCssText(blocks[j]), blocks[j].getAttribute('media') || '')
  }
}

/** CSS 文本落地: 优先可构造样式表(CSSOM, 不受 CSP 限制), 老浏览器退化为 <style> 元素 */
export function adoptStyleText(doc, cssText, media) {
  if (!cssText || !cssText.trim() || !doc) return
  if (typeof CSSStyleSheet === 'undefined' || !doc.adoptedStyleSheets) {
    var s = doc.createElement('style')
    if (media) s.media = media
    s.textContent = cssText
    ;(doc.head || doc.documentElement).appendChild(s)
    return
  }
  var sheet = new CSSStyleSheet()
  if (media) sheet.media.mediaText = media
  sheet.replace(cssText).catch(function (err) {
    console.warn('[csp-style-shim] 样式块解析失败已跳过:', err && err.message ? err.message : err)
  })
  doc.adoptedStyleSheets = Array.prototype.slice.call(doc.adoptedStyleSheets).concat([sheet])
}

/**
 * 还原为规范 HTML(HtmlEditor 等序列化回写 OSS 前调用):
 * 剥离注入的垫片脚本 + template[data-csp-style-block] → <style>。
 * style 属性无需还原 —— cssText 回填已反射回 DOM 序列化输出。
 */
export function restoreCanonicalHtml(html) {
  if (!html) return html
  var out = html.replace(/<script data-csp-shim[^>]*>[\s\S]*?<\/script>\s*/gi, '')
  out = out.replace(/<template\b([^>]*)>([\s\S]*?)<\/template>/gi, function (m, attrs, css) {
    if (attrs.indexOf(DATA_BLOCK) === -1) return m
    var rest = attrs.split(/\s+/).filter(function (a) { return a && a.indexOf(DATA_BLOCK) !== 0 }).join(' ')
    return '<style' + (rest ? ' ' + rest : '') + '>' + css + '<\/style>'
  })
  return out
}

/**
 * 全局垫片: 把 setAttribute('style', v) 重定向到 CSSOM(cssText)。
 * 覆盖第三方库(不经 deinline 预处理)的运行时 style 属性写入, 严格 CSP 下不再被拦。
 */
export function installSetAttributeStyleShim() {
  if (typeof Element === 'undefined' || Element.prototype.__cspStyleShim) return
  var orig = Element.prototype.setAttribute
  Object.defineProperty(Element.prototype, '__cspStyleShim', { value: true })
  Element.prototype.setAttribute = function (name, value) {
    if (typeof name === 'string' && name.toLowerCase() === 'style') {
      this.style.cssText = value
      return undefined
    }
    return orig.call(this, name, value)
  }
}

/**
 * 注入 srcdoc 的自包含回填脚本(组装流水线在 </body> 前插入, stampNonce 会为其补 nonce)。
 * 内容: iframe 内安装 setAttribute 垫片 + 立即回填一次(DOMContentLoaded 再兜底一次) +
 * 暴露 window.__cspApplyDeinlinedStyles 供运行时后续注入内容后重放。
 */
export var DEINLINE_REAPPLY_SCRIPT = '<script data-csp-shim>(function () {' +
  'var DS = "data-csp-style", DB = "data-csp-style-block";' +
  'function apply(root) {' +
  '  root = root || document;' +
  '  var marked = root.querySelectorAll("[" + DS + "]");' +
  '  for (var i = 0; i < marked.length; i++) {' +
  '    var el = marked[i]; var v = el.getAttribute(DS);' +
  '    if (v) el.style.cssText = v;' +
  '    el.removeAttribute(DS);' +
  '  }' +
  '  var blocks = root.querySelectorAll("template[" + DB + "]");' +
  '  for (var j = 0; j < blocks.length; j++) {' +
  '    var t = blocks[j];' +
  '    var css = (t.content ? t.content.textContent : "") || t.textContent || "";' +
  '    if (!css || !css.replace(/\\s/g, "")) continue;' +
  '    var doc = t.ownerDocument;' +
  '    if (typeof CSSStyleSheet !== "undefined" && doc.adoptedStyleSheets) {' +
  '      var sheet = new CSSStyleSheet();' +
  '      var media = t.getAttribute("media");' +
  '      if (media) sheet.media.mediaText = media;' +
  '      sheet.replace(css).catch(function () {});' +
  '      doc.adoptedStyleSheets = Array.prototype.slice.call(doc.adoptedStyleSheets).concat([sheet]);' +
  '    } else {' +
  '      var s = doc.createElement("style");' +
  '      var md = t.getAttribute("media");' +
  '      if (md) s.media = md;' +
  '      s.textContent = css;' +
  '      (doc.head || doc.documentElement).appendChild(s);' +
  '    }' +
  '  }' +
  '}' +
  'if (typeof Element !== "undefined" && !Element.prototype.__cspStyleShim) {' +
  '  var orig = Element.prototype.setAttribute;' +
  '  Object.defineProperty(Element.prototype, "__cspStyleShim", { value: true });' +
  '  Element.prototype.setAttribute = function (name, value) {' +
  '    if (typeof name === "string" && name.toLowerCase() === "style") { this.style.cssText = value; return undefined; }' +
  '    return orig.call(this, name, value);' +
  '  };' +
  '}' +
  'window.__cspApplyDeinlinedStyles = apply;' +
  'apply(document);' +
  'document.addEventListener("DOMContentLoaded", function () { apply(document); });' +
  '})()<\/script>'
